// MindForge web — English dictionary.
// Keys are namespaced; values are strings or functions that take placeholders.
// Spanish dictionary (es.ts) must mirror this shape exactly.

import type { Dict } from "../types";

export const en: Dict = {
  // ─── Navigation / shared ──────────────────────────────────────────
  "common.menu": "MENU",
  "common.back": "BACK TO MENU",
  "common.start": "START",
  "common.submit": "Submit",
  "common.enter": "ENTER",
  "common.continue": "Continue",
  "common.undo": "Undo",
  "common.backspace": "Backspace",
  "common.correct": "Correct!",
  "common.wrong": "Wrong!",
  "common.hurry": ({ seconds }: { seconds: number }) => `Hurry! ${seconds}s`,
  "common.true": "TRUE",
  "common.false": "FALSE",
  "common.score": "SCORE",
  "common.streak": "STREAK",
  "common.high": "HIGH",
  "common.match": "MATCH",
  "common.matched": "MATCHED",
  "common.getReady": "Get Ready",
  "common.answer": ({ answer }: { answer: string }) => `Answer: ${answer}`,

  // ─── Main menu ────────────────────────────────────────────────────
  "menu.header": ({ user }: { user: string }) =>
    `MindForge · Beta · Logged in as ${user}`,
  "menu.levelOfFifty": "/ 50",
  "menu.xpSuffix": "XP",
  "menu.nextXp": ({ amount }: { amount: string }) => `Next: ${amount} XP`,
  "menu.sessions": ({ count }: { count: number }) =>
    `${count} session${count === 1 ? "" : "s"} played`,
  "menu.unlocksAt": ({ level }: { level: number }) =>
    `UNLOCKS AT LEVEL ${level}`,
  "menu.highDiff": ({ high, diff }: { high: number; diff: number }) =>
    `HIGH · ${high} — DIFF ${diff}`,
  "menu.reset": "RESET MY PROGRESS",
  "menu.resetConfirm":
    "Reset all your progress?\n\nXP, levels, mode difficulties, and high scores will be wiped. This cannot be undone.",
  "menu.betaNotice":
    "BETA · all 7 modes are open from level 1 for testing.",
  "menu.savesNotice":
    "Saves stay on this device — clearing browser data wipes your progress. Bugs or feedback → message Jose.",

  // ─── Instructions screen (shared) ─────────────────────────────────
  "instructions.difficulty": "Difficulty",
  "instructions.max": ({ max }: { max: number }) => `MAX ${max}`,
  "instructions.decreaseDifficulty": "Decrease difficulty",
  "instructions.increaseDifficulty": "Increase difficulty",

  // ─── Results screen ───────────────────────────────────────────────
  "results.sessionComplete": ({ modeName }: { modeName: string }) =>
    `${modeName} · Session complete`,
  "results.title": "Results",
  "results.score": "Score",
  "results.accuracy": "Accuracy",
  "results.xpEarned": "XP Earned",
  "results.bestStreak": "Best Streak",
  "results.level": "Level",
  "results.progress": "Progress",
  "results.difficultyUp": "↑ Difficulty increased",
  "results.newUnlocks": "New modes unlocked",

  // ─── Mode metadata (names + descriptions) ─────────────────────────
  "mode.digit_span.name": "Digit Span",
  "mode.digit_span.description": "Remember sequences of digits",
  "mode.spatial.name": "Spatial Recall",
  "mode.spatial.description": "Remember grid positions in order",
  "mode.dual_task.name": "Dual Task",
  "mode.dual_task.description": "Letters + math simultaneously",
  "mode.nback.name": "N-Back",
  "mode.nback.description": "Update and track letter sequences",
  "mode.go_nogo.name": "Go / No-Go",
  "mode.go_nogo.description": "React fast, suppress impulse",
  "mode.rule_switch.name": "Rule Switch",
  "mode.rule_switch.description": "Adapt when the rules change",
  "mode.chunking.name": "Chunking Challenge",
  "mode.chunking.description": "Master memory grouping",

  // ─── Digit Span ───────────────────────────────────────────────────
  "digitSpan.memorize": "Memorize",
  "digitSpan.typeForward": "Type the sequence",
  "digitSpan.typeReverse": "Type the sequence in REVERSE",
  "digitSpan.instr.show": ({ n }: { n: number }) =>
    `A sequence of ${n} digits will appear one at a time.`,
  "digitSpan.instr.watch": "Watch carefully and memorize the order.",
  "digitSpan.instr.reverse":
    "Type them back in REVERSE order — last digit first.",
  "digitSpan.instr.forward":
    "Then type them back in the correct sequence.",
  "digitSpan.instr.keyboard":
    "Press ENTER to submit, BACKSPACE to correct mistakes. Physical keyboard works too.",
  "digitSpan.diffDesc": ({ n, reverse }: { n: number; reverse: boolean }) =>
    `${n}-digit sequences · ${reverse ? "REVERSE recall" : "forward recall"}`,

  // ─── Spatial Recall ───────────────────────────────────────────────
  "spatial.watch": "Watch the sequence",
  "spatial.tap": "Tap cells in order",
  "spatial.cellLabel": ({ row, col }: { row: number; col: number }) =>
    `Row ${row}, column ${col}`,
  "spatial.instr.watch": ({ cells, grid }: { cells: number; grid: number }) =>
    `Watch ${cells} cells light up on a ${grid}×${grid} grid.`,
  "spatial.instr.tap":
    "When the sequence ends, tap the same cells back in order.",
  "spatial.instr.undo":
    "You can undo your last tap. Submit when you've placed all cells.",
  "spatial.instr.threshold": "70% accuracy or higher counts as correct.",
  "spatial.diffDesc": ({ grid, cells }: { grid: number; cells: number }) =>
    `Grid ${grid}×${grid} · ${cells} cells`,

  // ─── Dual Task ────────────────────────────────────────────────────
  "dualTask.rememberLetter": "Remember this letter",
  "dualTask.letterOf": ({ index, total }: { index: number; total: number }) =>
    `Letter ${index} of ${total}`,
  "dualTask.trueOrFalse": "True or False?",
  "dualTask.wrongMath": ({ penalty }: { penalty: number }) =>
    `Wrong! −${penalty} XP`,
  "dualTask.recall": "Enter the letters in order",
  "dualTask.mathScore": ({ correct, total }: { correct: number; total: number }) =>
    `Math: ${correct}/${total} correct`,
  "dualTask.instr.show": ({ n }: { n: number }) =>
    `You'll see ${n} letters one at a time. Memorize each one.`,
  "dualTask.instr.math":
    "After each letter, a math problem appears — answer TRUE or FALSE.",
  "dualTask.instr.penalty": "Wrong math answers cost XP immediately.",
  "dualTask.instr.recall":
    "After all pairs, type the letters back in order. Letters must be right AND math ≥ 50%.",
  "dualTask.diffDesc": ({ n }: { n: number }) => `${n} letter–math pairs`,

  // ─── N-Back ───────────────────────────────────────────────────────
  "nback.modeTitle": ({ n }: { n: number }) => `${n}-Back`,
  "nback.matchHint": ({ n }: { n: number }) =>
    `Press MATCH if this letter appeared ${n} step${n === 1 ? "" : "s"} ago.`,
  "nback.instr.appear": ({ n }: { n: number }) =>
    `Letters appear one at a time. Press MATCH when the current letter equals the one ${n} step${n === 1 ? "" : "s"} ago.`,
  "nback.instr.firstN":
    "First N letters can never be matches — there's nothing to compare against yet.",
  "nback.instr.threshold":
    "70% accuracy passes. N adjusts between sessions based on how you do.",
  "nback.diffDesc": ({ n, count }: { n: number; count: number }) =>
    `N = ${n} · ${count} stimuli`,
  "nback.sequenceComplete": "Sequence Complete!",
  "nback.sequenceFailed": "Sequence Failed!",
  "nback.accuracy": ({ pct }: { pct: number }) => `Accuracy: ${pct}%`,
  "nback.hits": "hits",
  "nback.misses": "misses",
  "nback.falseAlarms": "false",
  "nback.nLevelChange": ({ from, to }: { from: number; to: number }) =>
    `N-level: ${from} → ${to}`,

  // ─── Go / No-Go ───────────────────────────────────────────────────
  "goNoGo.go": "GO",
  "goNoGo.stop": "STOP",
  "goNoGo.tapHint": "Tap anywhere on the screen for GO shapes only.",
  "goNoGo.hits": "HITS",
  "goNoGo.missesShort": "MISSES",
  "goNoGo.falseAlarms": "FA",
  "goNoGo.instr.go": "GO shapes are green circles. Tap as fast as you can.",
  "goNoGo.instr.noGo": "NO-GO shapes are red squares. DO NOT tap them.",
  "goNoGo.instr.run":
    "Each round runs through 20+ stimuli. 70% accuracy passes.",
  "goNoGo.instr.pace": "Pacing tightens as difficulty climbs.",
  "goNoGo.diffDesc": ({ count, pct }: { count: number; pct: number }) =>
    `${count} stimuli · ${pct}% nogo rate`,
  "goNoGo.sequenceComplete": "Sequence Complete!",
  "goNoGo.sequenceFailed": "Sequence Failed!",

  // ─── Rule Switch ──────────────────────────────────────────────────
  "ruleSwitch.streak": ({ n }: { n: number }) => `Streak: ${n}`,
  "ruleSwitch.target": ({ index }: { index: number }) => `Target ${index}`,
  "ruleSwitch.tapHint": "Tap a target card to sort.",
  "ruleSwitch.hint.color": "Think about COLORS…",
  "ruleSwitch.hint.shape": "Focus on SHAPES…",
  "ruleSwitch.hint.count": "Count the items…",
  "ruleSwitch.instr.targets":
    "There are 4 target cards at the top, one current card at the bottom.",
  "ruleSwitch.instr.sort":
    "Sort the current card to a target by matching one rule — color, shape, or count.",
  "ruleSwitch.instr.figureOut":
    "The rule isn't shown; figure it out from the feedback.",
  "ruleSwitch.instr.switch": ({ n }: { n: number }) =>
    `After ${n} correct in a row, the rule silently changes.`,
  "ruleSwitch.diffDesc": ({ n }: { n: number }) =>
    `Rule switches every ${n} correct`,

  // ─── Chunking Challenge ───────────────────────────────────────────
  "chunking.memorize": "Memorize this sequence",
  "chunking.seeChunks": "Now see the chunks",
  "chunking.enterFull": "Enter the full sequence",
  "chunking.instr.study": ({ n }: { n: number }) =>
    `Study a raw sequence of ${n} characters for a few seconds.`,
  "chunking.instr.chunks":
    "Then see the same sequence grouped into color-coded chunks.",
  "chunking.instr.recall":
    "Finally, type the full sequence back. 70% accuracy passes.",
  "chunking.instr.why":
    "Chunking is the single most useful memory technique we have.",
  "chunking.diffDesc": ({ length, size }: { length: number; size: number }) =>
    `Sequence ${length} long · chunks of ${size}`,
  "chunking.tip.0": "Group items into meaningful patterns (like phone numbers).",
  "chunking.tip.1": "Try to find words or abbreviations within letter groups.",
  "chunking.tip.2": "Associate each chunk with a vivid mental image.",
  "chunking.tip.3": "Use rhythm: say each chunk as a 'beat' in a song.",
  "chunking.tip.4": "Create stories linking your chunks together.",
  "chunking.tip.5":
    "Look for number patterns: ascending, descending, or repeated.",

  // ─── Locale toggle ────────────────────────────────────────────────
  "locale.label": "Language",
  "locale.en": "EN",
  "locale.es": "ES",

  // ─── Theme toggle ─────────────────────────────────────────────────
  "theme.label": "Theme",
  "theme.light": "White",
  "theme.dark": "Dark",

  // ─── First-run onboarding ─────────────────────────────────────────
  "onboarding.skip": "Skip",
  "onboarding.next": "Next",
  "onboarding.start": "Get started",
  "onboarding.welcome.title": "Welcome to MindForge",
  "onboarding.welcome.body":
    "A gym for working memory — the mental buffer behind focus, reading, and problem-solving. Short daily sessions, no setup.",
  "onboarding.how.title": "How it works",
  "onboarding.how.body":
    "Start with Digit Span. Play a few minutes a day. Hit 80% accuracy and the difficulty climbs to match you. Earn XP to unlock six more modes.",
  "onboarding.expect.title": "What to expect",
  "onboarding.expect.body":
    "You'll get measurably sharper at these exercises. We don't promise to make you smarter overall — just a precise workout for a precise skill.",
};
